/**
 * RankedMarginTable (#2302) — the two-level Kultur→Schlag tree over the
 * ``rollupByKultur`` groups. Level 1 = one row per Kultur+Sorte (5 columns:
 * Kultur | Fläche | Leistung | Direktkosten | DB1/ha, the per-ha being the
 * rollup's AREA-WEIGHTED Σdb1/Σarea); expanding a group reveals its ranked
 * Schlag leaves, each keeping the stable ``#cc-<crop_cycle_id>`` anchor.
 * A leaf click drives ``?cc=`` — selection now opens the side panel
 * (RowDetailPanel, page-rendered) instead of the old inline accordion; the
 * old "Gesamt" tfoot is gone — #2302 deleted it because MarginSummary's hero Σ
 * owned that answer, and HOF-20 has since deleted the hero too. **The page
 * therefore has no total anywhere, deliberately** (decided 2026-08-23): do not
 * rebuild this tfoot on the strength of the sentence above.
 * Expansion is PAGE-owned (controlled): the page force-includes the ?cc
 * row's parent so deep links keep working. The #2221 interactive column
 * sort applies to the Level-1 groups; members stay in rank order beneath
 * their parent. All strings hardcoded German du-form. (#2345 dropped the
 * per-group "ohne Gemeinkosten" badge because the Ampel verdict carried that
 * signal once; HOF-20 retired the Ampel, and the signal is deliberately NOT
 * rehomed at group level — RowDetail still spells it out per Schlag, and that
 * is where it stays.)
 */
import { useMemo, useState } from 'react';
import { BASIS_META, db1PerBasis, db1PerBasisForGroup, type MarginBasis } from './basis';
import {
  directCostTotal,
  sortKulturRollups,
  KULTUR_NUMERIC_KEYS,
  type KulturSortKey,
  type MarginSortDir,
} from './deckungsbeitragRollup';
// #2695 E — the Leistung column is where a MODELLED Leistung and real booked revenue sit in
// the same money column, one row apart, and nothing in the € itself tells them apart. The
// chip now lives in the shared ``estimateMark`` module (#2760), which the ranked chart and
// the hero mark from too — one wording, one pill, one CSS rule across every surface.
import { EstimatedChip } from './estimateMark';
import { formatEur, formatEurOrDash, formatHaOrDash } from './format';
// #2773 C — a row the default filter hid must say WHY the moment it is revealed.
// Same one-module discipline as EstimatedChip above.
import { classifyAnalysisUnit, classifyGroup, classifyRow } from './rowQuality';
import { RowQualityChips } from './rowQualityMark';
import type { AnalysisUnitRow, DeckungsbeitragRow, KulturRollupRow } from './types';
import './RankedMarginTable.css';

/** Header columns in render order — the 5-column Level-1 spec (#2302). */
interface KulturColumn {
  key: KulturSortKey;
  label: string;
  /** Right-aligned tabular-nums money/area column. */
  numeric: boolean;
  /** Container-query hide hook (Leistung/Direktkosten slim away on tight widths). */
  hideClass?: string;
}

const COLUMNS: KulturColumn[] = [
  { key: 'crop', label: 'Kultur', numeric: false },
  { key: 'area_ha', label: 'Fläche (ha)', numeric: true },
  { key: 'leistung', label: 'Leistung', numeric: true, hideClass: 'ranked-margin-table__leistung' },
  { key: 'direktkosten', label: 'Direktkosten', numeric: true, hideClass: 'ranked-margin-table__direktkosten' },
  // The ranking column's label follows the active Bezugsgröße (#2304): DB1/ha
  // (default), DB1/kg, DB1/lfm, DB1/Pflanze — computed at render, see columnLabel.
  { key: 'db1_per_ha', label: 'DB1/ha', numeric: true },
];

/** The ranking column relabels per basis (#2304) — a bare "DB1" for € gesamt; all other columns are static. */
const columnLabel = (col: KulturColumn, basis: MarginBasis): string => {
  if (col.key !== 'db1_per_ha') return col.label;
  const { unit } = BASIS_META[basis];
  return unit ? `DB1/${unit}` : 'DB1';
};

const COL_COUNT = COLUMNS.length;

/** Default view = the rollup's ranked DB1/ha order, descending. */
const DEFAULT_SORT: { key: KulturSortKey; dir: MarginSortDir } = { key: 'db1_per_ha', dir: 'desc' };

interface RankedMarginTableProps {
  /** Kultur+Sorte rollup groups of ONE period, pre-ranked by the rollup. */
  groups: KulturRollupRow[];
  /** ``?cc=`` — the selected Schlag leaf (== the open side panel). */
  selectedId?: string | null;
  /** Leaf click: the toggled selection (``null`` = deselect/close panel). */
  onSelect?: (cropCycleId: string | null) => void;
  /** Expanded group keys — page-owned; the ?cc parent arrives force-included. */
  expandedGroups: ReadonlySet<string>;
  onToggleGroup: (groupKey: string) => void;
  /** field_id → Schlag name (unresolved leaves read "Ohne Schlag"). */
  fieldNames: Record<string, string>;
  /** The active Bezugsgröße (#2304) — drives the ranking column label + values;
   *  ``groups`` arrive pre-ranked for it (rankKulturByBasis). Default ha. */
  basis?: MarginBasis;
  /**
   * HOF-195 — expanded Hauptschlag analysis units (``unit_key``), page-owned exactly like
   * ``expandedGroups``: the ?cc row's unit arrives force-included so a deep link into a
   * block lands on the open answer at BOTH levels. Optional, because a tenant with no
   * curated unit has no unit row to expand and every pre-HOF-195 caller stays valid.
   */
  expandedUnits?: ReadonlySet<string>;
  onToggleUnit?: (unitKey: string) => void;
}

/** No unit is expanded — a stable identity, so the default never re-renders the tree. */
const NO_UNITS: ReadonlySet<string> = new Set<string>();

export default function RankedMarginTable({
  groups,
  selectedId,
  onSelect,
  expandedGroups,
  onToggleGroup,
  fieldNames,
  basis = 'ha',
  expandedUnits = NO_UNITS,
  onToggleUnit,
}: RankedMarginTableProps) {
  // Interactive column sort (#2221 semantics) — component-local, NOT a URL
  // param. Default is the rollup's ranked DB1/ha order; a header click
  // re-sorts the PRE-RANKED groups so ties keep the rank tie-break.
  const [sort, setSort] = useState(DEFAULT_SORT);
  const isDefaultSort = sort.key === DEFAULT_SORT.key && sort.dir === DEFAULT_SORT.dir;

  const sorted = useMemo(
    () => (isDefaultSort ? groups : sortKulturRollups(groups, sort.key, sort.dir, basis)),
    [groups, isDefaultSort, sort.key, sort.dir, basis],
  );

  const toggleSort = (key: KulturSortKey) => {
    setSort((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      }
      // Numeric columns open desc (big first); the Kultur column opens asc (A→Z).
      return { key, dir: KULTUR_NUMERIC_KEYS.has(key) ? 'desc' : 'asc' };
    });
  };

  // The bold/active treatment follows the sorted column; in the default view
  // it lands on DB1/ha as before.
  const cellClass = (key: KulturSortKey, base: string): string =>
    sort.key === key ? `${base} ranked-margin-table__col--active` : base;

  /**
   * One Schlag leaf. ``nested`` marks a Level-3 block INSIDE a Hauptschlag analysis unit
   * (HOF-195) — the row is otherwise identical, anchor included, because a block is the
   * same crop_cycle whether or not a unit is drawn around it: ``?cc=`` addresses it the
   * same way and the side panel opens the same drawer.
   */
  const renderLeaf = (row: DeckungsbeitragRow, nested: boolean) => {
    const leafSelected = row.crop_cycle_id === selectedId;
    const toggleLeaf = () => onSelect?.(leafSelected ? null : row.crop_cycle_id);
    const leafClass = [
      'ranked-margin-table__row',
      'ranked-margin-table__leaf-row',
      // Leaves only render inside an open group → always in the block.
      'ranked-margin-table__row--open',
      row.db1 < 0 && row.leistung > 0 ? 'ranked-margin-table__row--loss' : '',
      leafSelected ? 'ranked-margin-table__row--selected' : '',
    ]
      .filter(Boolean)
      .join(' ');
    const fieldName = row.field_id ? fieldNames[row.field_id] : undefined;
    const labelClass = nested
      ? 'ranked-margin-table__leaf-field ranked-margin-table__leaf-field--nested'
      : 'ranked-margin-table__leaf-field';
    return (
      <tr
        id={`cc-${row.crop_cycle_id}`}
        key={row.derived_fact_id}
        className={leafClass}
        onClick={toggleLeaf}
      >
        <td className={cellClass('crop', labelClass)}>
          <button
            type="button"
            className="ranked-margin-table__leaf-button"
            aria-pressed={leafSelected}
            onClick={(e) => {
              e.stopPropagation();
              toggleLeaf();
            }}
          >
            {fieldName ?? 'Ohne Schlag'}
          </button>
          {/* Schlag grain: this row IS one cycle, so the copy is exact. */}
          <RowQualityChips quality={classifyRow(row)} grain="schlag" />
        </td>
        <td className={cellClass('area_ha', 'ranked-margin-table__num')}>
          {formatHaOrDash(row.area_ha)}
        </td>
        <td
          className={cellClass(
            'leistung',
            'ranked-margin-table__num ranked-margin-table__leistung',
          )}
        >
          {formatEur(row.leistung)}
          {row.leistung_estimated && <EstimatedChip grain="schlag" />}
        </td>
        <td
          className={cellClass(
            'direktkosten',
            'ranked-margin-table__num ranked-margin-table__direktkosten',
          )}
        >
          {formatEur(directCostTotal(row.direct_costs))}
        </td>
        <td className={cellClass('db1_per_ha', 'ranked-margin-table__num')}>
          {formatEurOrDash(db1PerBasis(row, basis))}
        </td>
      </tr>
    );
  };

  /**
   * HOF-195 — one Hauptschlag analysis unit at Level 2, plus its blocks at Level 3 when
   * it is open. Same five columns as every other row, so the Σ is read in the same places
   * the eye already looks; the quality chips come from ``classifyAnalysisUnit`` at grain
   * ``einheit``, never from the members' own verdicts, which is the whole point of the
   * level (#2809's grain-honest copy, one level down).
   */
  const renderUnit = (unit: AnalysisUnitRow) => {
    const unitExpanded = expandedUnits.has(unit.unit_key);
    const toggleUnit = () => onToggleUnit?.(unit.unit_key);
    const unitClass = [
      'ranked-margin-table__row',
      'ranked-margin-table__unit-row',
      'ranked-margin-table__row--open',
      unit.db1 < 0 && unit.leistung > 0 ? 'ranked-margin-table__row--loss' : '',
    ]
      .filter(Boolean)
      .join(' ');
    const unitRow = (
      <tr key={unit.unit_key} className={unitClass} onClick={toggleUnit}>
        <td className={cellClass('crop', 'ranked-margin-table__unit-cell')}>
          <div className="ranked-margin-table__crop-cell">
            <button
              type="button"
              className="ranked-margin-table__toggle"
              aria-expanded={unitExpanded}
              onClick={(e) => {
                e.stopPropagation();
                toggleUnit();
              }}
            >
              <span className="ranked-margin-table__caret" aria-hidden="true">
                {unitExpanded ? '▾' : '▸'}
              </span>
              <span className="ranked-margin-table__crop-label">
                <span className="ranked-margin-table__unit-name">{unit.hauptschlag}</span>
              </span>
            </button>
            <RowQualityChips quality={classifyAnalysisUnit(unit)} grain="einheit" />
            <span
              className="ranked-margin-table__count"
              title="Anzahl Schläge in dieser Analyseeinheit"
            >
              {unit.memberCount} {unit.memberCount === 1 ? 'Schlag' : 'Schläge'}
            </span>
          </div>
        </td>
        <td className={cellClass('area_ha', 'ranked-margin-table__num')}>
          {formatHaOrDash(unit.area_ha)}
        </td>
        <td className={cellClass('leistung', 'ranked-margin-table__num ranked-margin-table__leistung')}>
          {formatEur(unit.leistung)}
          {unit.leistung_estimated && <EstimatedChip grain="einheit" />}
        </td>
        <td
          className={cellClass(
            'direktkosten',
            'ranked-margin-table__num ranked-margin-table__direktkosten',
          )}
        >
          {formatEur(directCostTotal(unit.direct_costs))}
        </td>
        <td className={cellClass('db1_per_ha', 'ranked-margin-table__num')}>
          {formatEurOrDash(db1PerBasisForGroup(unit, basis))}
        </td>
      </tr>
    );
    if (!unitExpanded) return [unitRow];
    return [unitRow, ...unit.members.map((row) => renderLeaf(row, true))];
  };

  return (
    <div className="ranked-margin-table-scroll">
      <table className="ranked-margin-table">
      <thead>
        <tr>
          {COLUMNS.map((col) => {
            const active = sort.key === col.key;
            const thClass = [
              col.numeric ? 'ranked-margin-table__num' : '',
              col.hideClass ?? '',
              active ? 'ranked-margin-table__col--active' : '',
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <th
                key={col.key}
                scope="col"
                className={thClass || undefined}
                aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
              >
                <button
                  type="button"
                  className="ranked-margin-table__sort"
                  onClick={() => toggleSort(col.key)}
                >
                  {columnLabel(col, basis)}
                  {active && (
                    <span className="ranked-margin-table__sort-indicator" aria-hidden="true">
                      {sort.dir === 'asc' ? '▲' : '▼'}
                    </span>
                  )}
                </button>
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {sorted.length === 0 && (
          <tr>
            <td colSpan={COL_COUNT} className="ranked-margin-table__none">
              Keine Einträge in diesem Zeitraum.
            </td>
          </tr>
        )}
        {sorted.map((group) => {
          const expanded = expandedGroups.has(group.group_key);
          const toggleGroup = () => onToggleGroup(group.group_key);
          // Loss tint below the selection accent, as before: a Kultur that
          // sells but loses money reads red at a glance.
          const groupClass = [
            'ranked-margin-table__row',
            'ranked-margin-table__group-row',
            group.db1 < 0 && group.leistung > 0 ? 'ranked-margin-table__row--loss' : '',
            // Block-highlight (#2345): the open group + its leaves read as one
            // unfolded block.
            expanded ? 'ranked-margin-table__row--open' : '',
          ]
            .filter(Boolean)
            .join(' ');
          const groupRow = (
            <tr key={group.group_key} className={groupClass} onClick={toggleGroup}>
              <td className={cellClass('crop', 'ranked-margin-table__crop')}>
                {/* One flex row (#2345): caret+Kultur·Sorte on the left, the
                    amortisation hint inline, the Schlag-count chip pinned to the
                    right edge (margin-left:auto) so it never drifts with the
                    crop name's width. */}
                <div className="ranked-margin-table__crop-cell">
                  <button
                    type="button"
                    className="ranked-margin-table__toggle"
                    aria-expanded={expanded}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleGroup();
                    }}
                  >
                    <span className="ranked-margin-table__caret" aria-hidden="true">
                      {expanded ? '▾' : '▸'}
                    </span>
                    <span className="ranked-margin-table__crop-label">
                      <span className="ranked-margin-table__crop-name">{group.crop}</span>
                      {group.variety && (
                        <span className="ranked-margin-table__variety">· {group.variety}</span>
                      )}
                    </span>
                  </button>
                  {/* #2773 C — Kultur grain: some()-propagated reasons say "mindestens
                      ein Schlag" in their title (#2809). */}
                  <RowQualityChips quality={classifyGroup(group)} grain="kultur" />
                  {group.establishment_amortised > 0 && (
                    <span
                      className="ranked-margin-table__hint"
                      title="Enthält den auf dieses Jahr umgelegten Anteil der Etablierungskosten (Anlagejahr)."
                    >
                      inkl. Etablierungskosten
                    </span>
                  )}
                  <span
                    className="ranked-margin-table__count"
                    title="Anzahl Schläge in dieser Kultur"
                  >
                    {group.memberCount} {group.memberCount === 1 ? 'Schlag' : 'Schläge'}
                  </span>
                </div>
              </td>
              <td className={cellClass('area_ha', 'ranked-margin-table__num')}>
                {formatHaOrDash(group.area_ha)}
              </td>
              <td className={cellClass('leistung', 'ranked-margin-table__num ranked-margin-table__leistung')}>
                {formatEur(group.leistung)}
                {group.leistung_estimated && <EstimatedChip grain="kultur" />}
                {/* HOF-102 — no „bewertet" pill here any more. Every Leistung on this page
                    is a Preisspiegel valuation, so a chip saying so on every row marks
                    nothing. What earns a mark is the exception — a Menge with no price —
                    and that rides the ``leistung_unpriced`` reason chip above, propagated
                    to the group by ``classifyGroup``'s ``some()``. */}
              </td>
              <td
                className={cellClass(
                  'direktkosten',
                  'ranked-margin-table__num ranked-margin-table__direktkosten',
                )}
              >
                {formatEur(directCostTotal(group.direct_costs))}
              </td>
              <td className={cellClass('db1_per_ha', 'ranked-margin-table__num')}>
                {formatEurOrDash(db1PerBasisForGroup(group, basis))}
              </td>
            </tr>
          );
          if (!expanded) return [groupRow];
          return [groupRow, ...group.children.flatMap((child) =>
            child.kind === 'unit' ? renderUnit(child.unit) : [renderLeaf(child.row, false)],
          )];
        })}
      </tbody>
      </table>
    </div>
  );
}
